import { mutate, SWRConfig } from 'swr';

import { publicConfig } from '@public-config';

import { CommentHelpers } from '@diagram/helpers/CommentHelpers';
import { ComponentInstanceService } from '@diagram/services/ComponentInstanceService';
import { DiagramService } from '@diagram/services/DiagramService';
import { SelectionService } from '@diagram/services/SelectionService';
import { ViewportService } from '@diagram/services/ViewportService';
import { state as commentState } from '@diagram/state/comment';
import { state as commentsState } from '@diagram/state/comments';

import type { DiagramComment } from 'models';
import { getElementPosition } from 'helpers/dom/get-element-position';

import { ApiService } from 'services/ApiService';
import { TrackingService } from 'services/TrackingService';
import { ImpersonationService } from 'src/frontend/services/ImpersonationService';

const CommentService = {
    getComments: async (diagramId: string) => {
        return ApiService.get<DiagramComment[]>(`${publicConfig.urls.api}/projectDesignDiagrams/${diagramId}/comments`);
    },

    refreshComments: async (diagramId: string) => {
        console.log('refreshComments', diagramId);
        await mutate(CommentHelpers.swr.comments(diagramId));
    },

    getCachedComments: (): DiagramComment[] => {
        const diagramId = DiagramService.getId();
        const key = CommentHelpers.swr.comments(diagramId);

        return SWRConfig.defaultValue.cache.get(key)?.data || [];
    },

    getCachedComment: (commentId: DiagramComment['id']) => {
        if (commentId === 'new') {
            return commentState.newComment;
        }

        const comments = CommentService.getCachedComments();

        return comments.find((comment) => comment.id === commentId);
    },

    setCachedComment: (comment: DiagramComment) => {
        const diagramId = DiagramService.getId();
        const key = CommentHelpers.swr.comments(diagramId);

        const cache = SWRConfig.defaultValue.cache;
        const cachedComments = cache.get(key);

        if (cachedComments) {
            const index = cachedComments.data.findIndex((needle: DiagramComment) => {
                return needle.id === comment.id;
            });

            if (index !== -1) {
                Object.entries(comment).forEach(([key, value]) => {
                    cachedComments.data[index][key] = value;
                });

                cache.set(key, cachedComments);
            }
        }
    },

    createPlaceholder: ({
        position,
        componentInstances,
    }: {
        position: DiagramComment['position'];
        componentInstances: DiagramComment['componentInstances'];
    }) => {
        commentState.newComment = {
            id: 'new',
            position,
            componentInstances,
            subscribers: [],
            thread: [],
            date: new Date(),
            dateUpdated: new Date(),
            user: '',
            seen: true,
            status: 'created',
        };

        return commentState.newComment;
    },

    activatePlaceholder: () => {
        const center = ViewportService.getContainerCenter();
        const absoluteCenter = ViewportService.getLocalCoordinates({
            x: center.x - 20, // 20 is half of the bubble size
            y: center.y - 20, // 20 is half of the bubble size
        });

        CommentService.createPlaceholder({
            position: absoluteCenter,
            componentInstances: [],
        });
        CommentService.activateCanvasComment('new');
    },

    create: async ({
        diagramId = DiagramService.getId(),
        position,
        componentInstances = [],
        content,
        files = [],
    }: {
        diagramId?: string;
        position: DiagramComment['position'];
        componentInstances: DiagramComment['componentInstances'];
        content: string;
        files: { file: string }[];
    }) => {
        TrackingService.trackDiagramComment('comment');

        commentState.newCommentLoading = true;

        const { doc: createdComment } = await ApiService.post(
            `${publicConfig.urls.api}/diagramComments`,
            {
                diagram: diagramId,
                position,
                componentInstances,
            },
            { throwError: true },
        );

        const comment = await CommentService.addReply({
            comment: createdComment,
            content,
            files,
        });

        CommentService.activateCanvasComment(comment.id);
        commentState.newCommentLoading = false;

        return comment;
    },

    addReply: async ({
        comment,
        content,
        files,
    }: {
        comment: DiagramComment;
        content: string;
        files: { file: string }[];
    }): Promise<DiagramComment> => {
        if (comment.id === 'new') {
            return CommentService.create({
                position: comment.position,
                componentInstances: comment.componentInstances,
                content,
                files,
            });
        }

        const { comment: updatedComment } = await ApiService.post(
            `${publicConfig.urls.api}/diagramComments/${comment.id}/reply`,
            {
                content,
                files,
            },
        );

        return updatedComment as DiagramComment;
    },

    activateSidebarComment: (commentId: DiagramComment['id']) => {
        CommentService.view(commentId).then();

        commentState.sidebar = commentId;
        commentState.newComment = null;
    },

    deactivateSidebarComment: () => {
        commentState.sidebar = null;
    },

    activateCanvasPreview: (commentId: DiagramComment['id']) => {
        commentState.preview = commentId;
        commentState.newComment = null;
    },

    deactivateCanvasPreview: () => {
        commentState.preview = null;
    },

    activateCanvasComment: (commentId: DiagramComment['id']) => {
        CommentService.view(commentId).then();

        if (commentId !== 'new') {
            commentState.newComment = null;
        }

        commentState.canvas = commentId;
    },

    deactivateCanvasComment: () => {
        commentState.canvas = null;
    },

    activateOrDeactivateCanvasComment: (commentId: DiagramComment['id']) => {
        if (commentState.canvas === commentId) {
            CommentService.deactivateCanvasComment();
        } else {
            CommentService.activateCanvasComment(commentId);
        }
    },

    move: async ({
        id,
        position,
    }: {
        id: DiagramComment['id'];
        position: {
            x: number;
            y: number;
        };
    }) => {
        const comment = CommentService.getCachedComment(id)!;
        let linkedComponentInstance = null;

        const commentDOMElement = document.querySelector(`[data-selectable-id="${comment.id}"]`)!;
        const commentPosition = getElementPosition(commentDOMElement, 'bottom-left');
        const [componentInstanceWithDistance] = ComponentInstanceService.getAllWithDistanceFromPoint(commentPosition);

        if (componentInstanceWithDistance && componentInstanceWithDistance.distance <= 100) {
            linkedComponentInstance = componentInstanceWithDistance.componentInstance.id;
        }

        const next: DiagramComment = {
            ...comment,
            position,
            componentInstances: linkedComponentInstance ? [linkedComponentInstance] : [],
        };

        if (next.id === 'new') {
            commentState.newComment = next;

            return;
        }

        CommentService.setCachedComment(next);

        ApiService.patch(`${publicConfig.urls.api}/diagramComments/${comment.id}`, {
            position,
            componentInstances: linkedComponentInstance ? [linkedComponentInstance] : [],
        }).then();
    },

    moveIntoView: (comment: DiagramComment, withPopup = true) => {
        const BUBBLE_SIZE = 40;
        const POPUP_WIDTH = withPopup ? 320 : 0;
        const PADDING = withPopup ? 8 : 0;

        ViewportService.moveIntoView(comment.position, {
            x: -(BUBBLE_SIZE + PADDING + POPUP_WIDTH) / 2,
            y: -BUBBLE_SIZE / 2,
        });
    },

    view: async (commentId: DiagramComment['id']) => {
        if (commentId !== 'new' && !ImpersonationService.isImpersonating()) {
            await ApiService.post(`${publicConfig.urls.api}/diagramComments/${commentId}/view`);
        }
    },

    unlinkComponentInstance: (comment: DiagramComment) => {
        const next: DiagramComment = {
            ...comment,
            componentInstances: [],
        };

        CommentService.setCachedComment(next);

        ApiService.patch(`${publicConfig.urls.api}/diagramComments/${comment.id}`, {
            componentInstances: [],
        }).then();
    },

    resolve: (comment: DiagramComment): DiagramComment => {
        const next: DiagramComment = {
            ...comment,
            status: comment.status === 'resolved' ? 'activated' : 'resolved',
        };

        CommentService.setCachedComment(next);

        SelectionService.clear();
        ApiService.post(`${publicConfig.urls.api}/diagramComments/${comment.id}/toggle-resolved`).then();

        return next;
    },

    subscribe: async (comment: DiagramComment) => {
        await ApiService.post(`${publicConfig.urls.api}/diagramComments/${comment.id}/subscribe`).then();
    },

    unsubscribe: async (comment: DiagramComment) => {
        await ApiService.post(`${publicConfig.urls.api}/diagramComments/${comment.id}/unsubscribe`).then();
    },

    delete: (comment: DiagramComment): DiagramComment => {
        const next: DiagramComment = {
            ...comment,
            status: comment.status === 'deleted' ? 'activated' : 'deleted',
        };

        CommentService.setCachedComment(next);

        SelectionService.clear();
        ApiService.post(`${publicConfig.urls.api}/diagramComments/${comment.id}/toggle-deleted`).then();

        return next;
    },

    isDeletable: (comment: DiagramComment) => {
        return comment.status !== 'deleted';
    },

    sortByLatestActivity: () => {
        commentsState.sorting = 'latest-activity';
    },

    sortByNewest: () => {
        commentsState.sorting = 'newest';
    },

    sortByOldest: () => {
        commentsState.sorting = 'oldest';
    },

    toggleResolvedComments: () => {
        commentsState.filters.resolved = !commentsState.filters.resolved;
    },

    toggleDeletedComments: () => {
        commentsState.filters.deleted = !commentsState.filters.deleted;
    },
};

export { CommentService };
